import { handleAxiosError } from '@/api/helpers';
import { useBeeFreeToken } from '@/hooks/query/bee-free.hook';
import { useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import { LoadingPage } from '@/pages';
import { selectCurrentAccount } from '@/store/features/account';
import BeefreeSDK, { beeTypes } from '@beefree.io/sdk';
import { AxiosError } from 'axios';
import { t } from 'i18next';
import React, { useEffect, useRef, useState } from 'react';

type BeePluginPageContainerProps = {
  template: any;
  handleChange: (page: beeTypes.IEntityContentJson) => void;
  handleSave: (page: object, html: string) => void;
  handleSendTest?: (html: string) => void;
};

export const BeePluginPageContainer: React.FC<BeePluginPageContainerProps> = ({
  template,
  handleChange,
  handleSave,
  handleSendTest,
}) => {
  const account = useAppSelector(selectCurrentAccount);
  const toast = useToast();
  const ref = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [beeToken, setBeeToken] = useState<beeTypes.IToken | undefined>();

  const { create: getBeeFreeToken } = useBeeFreeToken();

  // If the plugin was already initialized and the token is present, don't get it
  useEffect(() => {
    if (!account) {
      return;
    }

    const getToken = async () => {
      setIsLoading(true);
      const token = await getBeeFreeToken({
        uid: account?.id as string,
        type: 'page',
      });

      setBeeToken(token);
      setIsLoading(false);
    };
    getToken();
  }, [account?.id]);

  useEffect(() => {
    if (!beeToken) {
      return;
    }

    const loadEditor = async () => {
      try {
        if (!account || !beeToken) {
          return;
        }

        // @todo: add placeholders with BeeFree fixes its types
        const beePageEditor = new BeefreeSDK(beeToken);
        beePageEditor.start(
          {
            container: 'beeplugin-editor',
            uid: account?.id as string,
            trackChanges: true,
            preventClose: true,
            onChange: (page) => {
              handleChange(JSON.parse(page));
            },
            onSend: (html) => {
              if (!handleSendTest) return;
              handleSendTest(html);
            },
            onSave: (page, html) => {
              // if (!handleSave) return;
              handleSave(JSON.parse(page), html);
            },
            commenting: true,
            defaultForm: {
              structure: {
                fields: {
                  username: {
                    type: 'text',
                    label: 'Username *',
                    canBeRemovedFromLayout: true,
                    attributes: {
                      required: true,
                      name: 'username',
                    },
                  },
                  email: {
                    type: 'email',
                    label: 'Email *',
                    canBeRemovedFromLayout: true,
                    removeFromLayout: true,
                    attributes: {
                      name: 'email',
                    },
                  },
                  password: {
                    type: 'password',
                    label: 'Password *',
                    canBeRemovedFromLayout: true,
                    attributes: {
                      required: true,
                      name: 'password',
                    },
                  },
                  date: {
                    type: 'date',
                    label: 'Date of birth',
                    removeFromLayout: true,
                    canBeRemovedFromLayout: true,
                    attributes: {
                      name: 'date',
                    },
                  },
                  tel: {
                    type: 'tel',
                    label: 'Phone number',
                    removeFromLayout: true,
                    canBeRemovedFromLayout: true,
                    attributes: {
                      name: 'phone',
                    },
                  },
                  url: {
                    type: 'url',
                    label: 'Website',
                    removeFromLayout: true,
                    canBeRemovedFromLayout: true,
                    attributes: {
                      name: 'url',
                    },
                  },
                  message: {
                    type: 'textarea',
                    label: 'Message',
                    removeFromLayout: true,
                    canBeRemovedFromLayout: true,
                    attributes: {
                      name: 'message',
                    },
                  },
                  gender: {
                    type: 'select',
                    label: 'Gender',
                    removeFromLayout: true,
                    canBeRemovedFromLayout: true,
                    attributes: {
                      name: 'gender',
                    },
                    options: [
                      {
                        type: 'option',
                        label: 'Male',
                        value: 'M',
                      },
                      {
                        type: 'option',
                        label: 'Female',
                        value: 'F',
                      },
                      {
                        type: 'option',
                        label: 'Not telling',
                        value: '-',
                      },
                    ],
                  },
                  privacy: {
                    type: 'checkbox',
                    label:
                      'Accept privacy policy. [Read it here](http://example.com)',
                    removeFromLayout: true,
                    canBeRemovedFromLayout: true,
                    attributes: {
                      required: true,
                      name: 'privacy',
                    },
                  },
                  submit: {
                    type: 'submit',
                    canBeRemovedFromLayout: true,
                    label: 'Submit',
                    attributes: {
                      value: 'SUBMIT',
                      name: 'submit_button',
                    },
                  },
                },
                layout: [
                  ['username'],
                  ['email'],
                  ['password'],
                  ['date'],
                  ['tel'],
                  ['url'],
                  ['gender'],
                  ['message'],
                  ['privacy'],
                  ['submit'],
                ],
                attributes: {
                  'accept-charset': 'UTF-8',
                  action: 'http://example.com/read-form-script',
                  autocomplete: 'off',
                  enctype: 'multipart/form-data',
                  method: 'post',
                  novalidate: false,
                  target: '_self',
                },
                title: 'Login form',
                description: 'Default login form',
              },
            },
            contentDefaults: {
              form: {
                labelsOptions: {
                  color: '#000000',
                },
                fieldsOptions: {
                  color: '#000000',
                  backgroundColor: '#ffffff',
                  outlineColor: '#3AAEE0',
                  borderRadius: '4px',
                  borderTop: '1px solid gray',
                  borderRight: '1px solid gray',
                  borderBottom: '1px solid gray',
                  borderLeft: '1px solid gray',
                },
                buttonsOptions: {
                  marginTop: '12px',
                  align: 'center',
                  width: 'auto',
                  margingLeft: 'auto',
                  marginRight: 'auto',
                },
              },
            },
          },
          template,
        );
      } catch (e) {
        handleAxiosError(e as Error | AxiosError, toast);
      }
    };

    loadEditor();
  }, [beeToken]);

  return (
    <div>
      {isLoading && <LoadingPage message={t('generic.loading')} />}
      {!isLoading && (
        <div id="beeplugin-editor" ref={ref} style={{ height: '100%' }}></div>
      )}
    </div>
  );
};
